import { useMemo } from 'react'
import { DailyTotals, MetricsSummary } from '@/types'
import { MetricPeriod } from '@/lib/utils'
import { PlanData } from './useStudioAnalytics'
import { MetricWeek } from './useMetricWeeks'

export interface WeekMetrics extends MetricsSummary {
  days_with_data?: number
  week_len_days?: number
}

export interface EconomicsMetrics {
  costPerLead: number
  costPerAppointment: number
  costPerVisit: number
  costPerClient: number
  avgClientValue: number
  romi: number
  marketingBudget: {
    fact: number
    plan: number
  }
}

export interface PlanVsFactMetrics {
  leads: { fact: number; plan: number; percentage: number }
  appointments: { fact: number; plan: number; percentage: number }
  visits: { fact: number; plan: number; percentage: number }
  sales: { fact: number; plan: number; percentage: number }
  revenue: { fact: number; plan: number; percentage: number }
  avgCheck: { fact: number; plan: number; percentage: number }
}

export interface ConversionMetrics {
  cr1: number // Leads -> Appointments
  cr2: number // Appointments -> Visits
  cr3: number // Visits -> Sales
  crTotal: number // Leads -> Sales
}

export interface UseAnalyticsMetricsParams {
  dailyData: DailyTotals[]
  planData: PlanData | null
  previousPeriodData: PlanData | null
  currentPeriod: MetricPeriod | null
  totalMetrics: MetricsSummary | null
  currentWeek: MetricWeek | null
  prevWeek: MetricWeek | null
}

export interface UseAnalyticsMetricsReturn {
  currentWeekMetrics: WeekMetrics | null
  economicsMetrics: EconomicsMetrics | null
  planVsFactMetrics: PlanVsFactMetrics | null
  conversionMetrics: ConversionMetrics | null
  isCurrentPeriod: boolean
}

/**
 * Hook для расчета различных метрик аналитики
 * Вычисляет конверсии, средние чеки, экономику, план/факт и т.д.
 */
export function useAnalyticsMetrics({
  dailyData,
  planData,
  previousPeriodData,
  currentPeriod,
  totalMetrics,
  currentWeek,
  prevWeek
}: UseAnalyticsMetricsParams): UseAnalyticsMetricsReturn {
  
  const TAG = '[useAnalyticsMetrics]'

  // Локальная дата (не UTC!) — иначе в UTC+N зонах toISOString() даёт предыдущий день
  const localToday = () => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
  }

  // Проверка, является ли период текущим
  const isCurrentPeriod = useMemo(() => {
    if (!currentPeriod) return false
    const today = localToday()
    return currentPeriod.period_start <= today && today <= currentPeriod.period_end
  }, [currentPeriod])

  /**
   * Находит последнее непустое значение total_clients_count в массиве дневных данных.
   * Данные могут быть не за каждый день — берём последнюю дату с не-null значением.
   */
  const getLastClientsCount = (data: DailyTotals[], label: string): { value: number | undefined; date: string | undefined } => {
    const filtered = data
      .filter(d => d.total_clients_count != null)
      .sort((a, b) => b.metric_date.localeCompare(a.metric_date))
    
    const value = filtered[0]?.total_clients_count ?? undefined
    const date = filtered[0]?.metric_date
    
    console.log(TAG, `👥 getLastClientsCount [${label}]:`, {
      totalDays: data.length,
      daysWithClients: filtered.length,
      allClientValues: data.map(d => `${d.metric_date}=${d.total_clients_count}`).join(', '),
      result: value !== undefined ? `${value} (дата: ${date})` : 'undefined (нет данных)'
    })
    
    return { value, date }
  }

  // Расчет метрик за текущую неделю
  const currentWeekMetrics = useMemo(() => {
    if (!dailyData.length || !totalMetrics || !currentPeriod || !isCurrentPeriod || !currentWeek) {
      console.log(TAG, '⏭️ Пропуск расчёта недели:', {
        dailyDataLen: dailyData.length,
        hasTotalMetrics: !!totalMetrics,
        hasCurrentPeriod: !!currentPeriod,
        isCurrentPeriod,
        currentWeek: currentWeek
          ? `#${currentWeek.week_number_in_period} [${currentWeek.week_start}..${currentWeek.week_end}]`
          : 'null'
      })
      return null
    }

    const weekStartStr = currentWeek.week_start
    const weekEndStr = currentWeek.week_end

    console.log(TAG, '📊 Расчёт недельных метрик:', {
      weekNumber: currentWeek.week_number_in_period,
      weekRange: `${weekStartStr} .. ${weekEndStr}`,
      weekSource: currentWeek.week_id < 0 ? 'computed (fallback)' : `metric_weeks (id=${currentWeek.week_id})`,
      prevWeek: prevWeek
        ? `#${prevWeek.week_number_in_period} [${prevWeek.week_start}..${prevWeek.week_end}]`
        : 'null'
    })

    // Фильтруем данные за текущую неделю
    const weekData = dailyData.filter(day =>
      day.metric_date >= weekStartStr && day.metric_date <= weekEndStr
    )

    console.log(TAG, '📅 Данные текущей недели:', {
      matchedDays: weekData.length,
      dates: weekData.map(d => d.metric_date)
    })

    if (weekData.length === 0) {
      console.log(TAG, '⚠️ Нет данных за текущую неделю')
      return null
    }
    
    // Фильтруем данные предыдущей недели (если есть)
    let prevWeekData: DailyTotals[] = []
    if (prevWeek) {
      prevWeekData = dailyData.filter(day =>
        day.metric_date >= prevWeek.week_start && day.metric_date <= prevWeek.week_end
      )
      console.log(TAG, '📅 Данные предыдущей недели:', {
        matchedDays: prevWeekData.length,
        dates: prevWeekData.map(d => d.metric_date)
      })
    }
    
    // Клиенты: последнее непустое значение текущей и предыдущей недели
    const currResult = getLastClientsCount(weekData, 'текущая неделя')
    const prevResult = prevWeekData.length > 0
      ? getLastClientsCount(prevWeekData, 'предыдущая неделя')
      : { value: undefined, date: undefined }
    
    const clientsNow = currResult.value || 0
    
    // Отклонение = последнее значение текущей недели - последнее значение предыдущей недели
    const clientsGrowthWeek = prevResult.value !== undefined 
      ? clientsNow - prevResult.value 
      : undefined

    console.log(TAG, '🔢 Расчёт клиентов за неделю:', {
      clientsAtCurrWeekEnd: currResult.value,
      clientsAtCurrWeekEndDate: currResult.date,
      clientsAtPrevWeekEnd: prevResult.value,
      clientsAtPrevWeekEndDate: prevResult.date,
      clientsGrowthWeek,
      formula: prevResult.value !== undefined
        ? `${clientsNow} - ${prevResult.value} = ${clientsGrowthWeek}`
        : 'нет данных пред. недели → growth=undefined'
    })

    // Рассчитываем суммы за неделю
    const weekTotals: WeekMetrics = {
      total_revenue: weekData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0),
      new_sales_amount: weekData.reduce(
        (sum, day) => sum + (day.new_sales_amount || 0) + (day.new_subscriptions_amount || 0) + (day.membership_fees_new_amount || 0),
        0
      ),
      renewals_amount: weekData.reduce((sum, day) => sum + (day.renewals_amount || 0), 0),
      returning_clients_amount: weekData.reduce((sum, day) => sum + (day.returning_clients_amount || 0), 0),
      total_visitors: weekData.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0),
      total_sales: weekData.reduce((sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0), 0),
      
      total_clients_count: clientsNow,
      clients_growth: clientsGrowthWeek,
      marketing_expenses: weekData.reduce((sum, day) => sum + (day.marketing_expenses_amount || 0), 0),
      avg_sale_amount: 0,
      conversion_rate: 0,
      days_with_data: weekData.length,
      week_len_days: currentWeek.week_len_days || 7
    }

    const weekNewSalesAmountWithoutFees = weekTotals.new_sales_amount - weekData.reduce(
      (sum, day) => sum + (day.membership_fees_new_amount || 0),
      0
    )
    weekTotals.avg_sale_amount = weekTotals.total_sales > 0
      ? weekNewSalesAmountWithoutFees / weekTotals.total_sales
      : 0
    weekTotals.conversion_rate = weekTotals.total_visitors > 0
      ? (weekTotals.total_sales / weekTotals.total_visitors) * 100
      : 0

    return weekTotals
  }, [dailyData, totalMetrics, currentPeriod, isCurrentPeriod, currentWeek, prevWeek])

  // Расчет экономических метрик
  const economicsMetrics = useMemo((): EconomicsMetrics | null => {
    if (!totalMetrics || !dailyData.length) {
      return null
    }

    const totalLeads = dailyData.reduce((sum, day) => sum + (day.leads_count || 0), 0)
    const totalAppointments = dailyData.reduce((sum, day) => sum + (day.appointments_count || 0), 0)
    const marketingExpenses = dailyData.reduce((sum, day) => sum + (day.marketing_expenses_amount || 0), 0)

    const costPerLead = totalLeads > 0 ? marketingExpenses / totalLeads : 0
    const costPerAppointment = totalAppointments > 0 ? marketingExpenses / totalAppointments : 0
    const costPerVisit = totalMetrics.total_visitors > 0 ? marketingExpenses / totalMetrics.total_visitors : 0
    const costPerClient = totalMetrics.total_sales > 0 ? marketingExpenses / totalMetrics.total_sales : 0
    const mixedNewRevenue = totalMetrics.new_sales_amount || 0
    const mixedNewRevenueWithoutFees = mixedNewRevenue - (totalMetrics.membership_fees_new_amount || 0)
    const avgClientValue = totalMetrics.total_sales > 0 ? mixedNewRevenueWithoutFees / totalMetrics.total_sales : 0
    const romi = marketingExpenses > 0 ? (mixedNewRevenue / marketingExpenses) * 100 : 0

    return {
      costPerLead,
      costPerAppointment,
      costPerVisit,
      costPerClient,
      avgClientValue,
      romi,
      marketingBudget: {
        fact: marketingExpenses,
        plan: planData?.marketing_budget_plan || 0
      }
    }
  }, [totalMetrics, dailyData, planData])

  // Расчет план/факт метрик
  const planVsFactMetrics = useMemo((): PlanVsFactMetrics | null => {
    if (!totalMetrics || !planData || !dailyData.length) {
      return null
    }

    const totalLeads = dailyData.reduce((sum, day) => sum + (day.leads_count || 0), 0)
    const totalAppointments = dailyData.reduce((sum, day) => sum + (day.appointments_count || 0), 0)

    const calculatePercentage = (fact: number, plan: number) => {
      return plan > 0 ? (fact / plan) * 100 : 0
    }

    return {
      leads: {
        fact: totalLeads,
        plan: planData.leads_count_plan || 0,
        percentage: calculatePercentage(totalLeads, planData.leads_count_plan || 0)
      },
      appointments: {
        fact: totalAppointments,
        plan: planData.appointments_count_plan || 0,
        percentage: calculatePercentage(totalAppointments, planData.appointments_count_plan || 0)
      },
      visits: {
        fact: totalMetrics.total_visitors,
        plan: planData.new_visitors_count_plan || 0,
        percentage: calculatePercentage(totalMetrics.total_visitors, planData.new_visitors_count_plan || 0)
      },
      sales: {
        fact: totalMetrics.total_sales,
        plan: (planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0),
        percentage: calculatePercentage(
          totalMetrics.total_sales,
          (planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)
        )
      },
      revenue: {
        fact: totalMetrics.total_revenue,
        plan: planData.total_gross_revenue_plan || 0,
        percentage: calculatePercentage(totalMetrics.total_revenue, planData.total_gross_revenue_plan || 0)
      },
      avgCheck: {
        fact: totalMetrics.avg_sale_amount,
        plan: (() => {
          const planCount = (planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)
          const planAmount =
            (planData.new_sales_amount_plan || 0) +
            (planData.new_subscriptions_amount_plan || 0)
          return planCount > 0 ? planAmount / planCount : 0
        })(),
        percentage: (() => {
          const planCount = (planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)
          const planAmount =
            (planData.new_sales_amount_plan || 0) +
            (planData.new_subscriptions_amount_plan || 0)
          const planAvg = planCount > 0 ? planAmount / planCount : 0
          return calculatePercentage(totalMetrics.avg_sale_amount, planAvg)
        })()
      }
    }
  }, [totalMetrics, planData, dailyData])

  // Расчет конверсий
  const conversionMetrics = useMemo((): ConversionMetrics | null => {
    if (!dailyData.length) {
      return null
    }

    const totalLeads = dailyData.reduce((sum, day) => sum + (day.leads_count || 0), 0)
    const totalAppointments = dailyData.reduce((sum, day) => sum + (day.appointments_count || 0), 0)
    const totalVisits = dailyData.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0)
    const totalSales = dailyData.reduce((sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0), 0)

    const cr1 = totalLeads > 0 ? (totalAppointments / totalLeads) * 100 : 0
    const cr2 = totalAppointments > 0 ? (totalVisits / totalAppointments) * 100 : 0
    const cr3 = totalVisits > 0 ? (totalSales / totalVisits) * 100 : 0
    const crTotal = totalLeads > 0 ? (totalSales / totalLeads) * 100 : 0

    return {
      cr1,
      cr2,
      cr3,
      crTotal
    }
  }, [dailyData])

  return {
    currentWeekMetrics,
    economicsMetrics,
    planVsFactMetrics,
    conversionMetrics,
    isCurrentPeriod
  }
}
